import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/HoverCard';
import { formatWorkspaceMetricValue } from '@/components/workspace/WorkspaceMetricCell';
import { workspaceTableClass } from '@/components/workspace/WorkspaceSection';
import {
  CAMPAIGN_PLACEMENT_LINES,
  formatPlacementBidPercent,
  parseCampaignPlacementBidPercents,
  placementHasActivity,
  placementDeltaTone,
  PLACEMENT_CARD_PRIMARY_METRICS,
  PLACEMENT_MORE_LEFT_METRICS,
  PLACEMENT_MORE_RIGHT_METRICS,
  type CampaignPlacementBidPercents,
  type PlacementCardMetricDef,
} from '@/utils/workspaceCampaignPlacement';
import {
  EMPTY_PLACEMENT_METRICS,
  type PlacementDerivedMetrics,
  type PlacementId,
} from '@/utils/workspacePlacementMetrics';
import {
  formatPctChangeLabel,
  percentChangeVsBaseline,
} from '@/api/snapshotMetrics';
import { cn } from '@/utils/cn';

function StackedPlacementLines({
  lines,
}: {
  lines: Array<{ label: string; value: string }>;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5 whitespace-normal text-body-sm leading-snug">
      {lines.map((line) => (
        <div key={line.label} className="min-w-0 truncate whitespace-nowrap">
          <span className="text-foreground-muted">{line.label}: </span>
          <span className="font-mono tabular-nums text-foreground">{line.value}</span>
        </div>
      ))}
    </div>
  );
}

export function WorkspaceCampaignPlacementBidsHeader() {
  return <th className={`${workspaceTableClass.th} text-center`}>Placement bids</th>;
}

export function WorkspaceCampaignPlacementMetricHeaders() {
  return (
    <>
      {CAMPAIGN_PLACEMENT_LINES.map((line) => (
        <th key={line.id} className={`${workspaceTableClass.th} text-center`}>
          {line.label}
        </th>
      ))}
    </>
  );
}

export function WorkspaceCampaignPlacementBidsCell({
  raw,
}: {
  raw: Record<string, unknown> | null | undefined;
}) {
  const percents: CampaignPlacementBidPercents = parseCampaignPlacementBidPercents(raw);
  return (
    <td className={`${workspaceTableClass.td} app-table-cell-wrap align-top whitespace-normal`}>
      <StackedPlacementLines
        lines={CAMPAIGN_PLACEMENT_LINES.map(({ id, label }) => ({
          label,
          value: formatPlacementBidPercent(percents[id]),
        }))}
      />
    </td>
  );
}

function asMetricColumn(def: PlacementCardMetricDef) {
  return {
    key: String(def.key),
    label: def.label,
    polarity: def.polarity,
    format: def.format,
  };
}

function CompactPlacementDelta({
  current,
  previous,
  polarity,
}: {
  current: number;
  previous: number;
  polarity: PlacementCardMetricDef['polarity'];
}) {
  const pct = percentChangeVsBaseline(current, previous);
  const label = formatPctChangeLabel(pct);
  if (pct.type !== 'ok') {
    return <span className="text-[11px] font-medium text-foreground-muted">{label}</span>;
  }

  const isUp = pct.value > 0;
  const tone = placementDeltaTone(current, previous, polarity);
  const Icon = isUp ? ChevronUp : ChevronDown;

  return (
    <span
      className={cn(
        'inline-flex items-center text-[11px] font-medium tabular-nums',
        tone === 'good' && 'text-success-ink',
        tone === 'bad' && 'text-error-ink',
        tone === 'neutral' && 'text-foreground-muted'
      )}
    >
      {pct.value !== 0 ? <Icon className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {label}
    </span>
  );
}

function PlacementHoverMetricRow({
  def,
  current,
  previous,
  compareEnabled,
  hasActivity,
}: {
  def: PlacementCardMetricDef;
  current: PlacementDerivedMetrics;
  previous: PlacementDerivedMetrics | null;
  compareEnabled: boolean;
  hasActivity: boolean;
}) {
  const value = Number(current[def.key]) || 0;
  const prevValue = previous ? Number(previous[def.key]) || 0 : 0;
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-3 text-body-sm">
      <span className="text-foreground-muted">{def.label}</span>
      <span className="text-right font-mono tabular-nums text-foreground">
        {hasActivity ? formatWorkspaceMetricValue(asMetricColumn(def), value) : '—'}
      </span>
      {compareEnabled && hasActivity ? (
        <CompactPlacementDelta current={value} previous={prevValue} polarity={def.polarity} />
      ) : (
        <span />
      )}
    </div>
  );
}

export function WorkspacePlacementMetricCard({
  title,
  current,
  previous,
  compareEnabled,
  contentClassName,
}: {
  title: string;
  current: PlacementDerivedMetrics | null | undefined;
  previous: PlacementDerivedMetrics | null | undefined;
  compareEnabled: boolean;
  contentClassName?: string;
}) {
  const active = placementHasActivity(current);
  const metrics = current ?? EMPTY_PLACEMENT_METRICS;
  const prev = previous ?? null;

  return (
    <HoverCard openDelay={150} closeDelay={100}>
      <div className="min-w-[11rem] rounded-lg border border-border bg-surface px-3 py-2">
        <div className="flex flex-col gap-1">
          {PLACEMENT_CARD_PRIMARY_METRICS.map((def) => {
            const value = Number(metrics[def.key]) || 0;
            const prevValue = prev ? Number(prev[def.key]) || 0 : 0;
            return (
              <div
                key={String(def.key)}
                className="grid grid-cols-[auto_1fr_auto] items-baseline gap-x-2 text-body-sm"
              >
                <span className="text-foreground-muted">{def.label}</span>
                <span className="text-right font-mono tabular-nums text-foreground">
                  {active ? formatWorkspaceMetricValue(asMetricColumn(def), value) : '—'}
                </span>
                {compareEnabled && active ? (
                  <CompactPlacementDelta
                    current={value}
                    previous={prevValue}
                    polarity={def.polarity}
                  />
                ) : (
                  <span className="w-0" />
                )}
              </div>
            );
          })}
        </div>
        <HoverCardTrigger asChild>
          <button
            type="button"
            className="mt-1.5 text-left text-xs font-medium text-foreground-muted underline decoration-dotted underline-offset-2 hover:text-foreground"
          >
            More
          </button>
        </HoverCardTrigger>
      </div>
      <HoverCardContent
        side="bottom"
        align="start"
        className={cn('w-[22rem] p-3', contentClassName)}
      >
        <div className="mb-2 text-sm font-semibold text-foreground">{title}</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <div className="flex flex-col gap-1">
            {PLACEMENT_MORE_LEFT_METRICS.map((def) => (
              <PlacementHoverMetricRow
                key={String(def.key)}
                def={def}
                current={metrics}
                previous={prev}
                compareEnabled={compareEnabled}
                hasActivity={active}
              />
            ))}
          </div>
          <div className="flex flex-col gap-1">
            {PLACEMENT_MORE_RIGHT_METRICS.map((def) => (
              <PlacementHoverMetricRow
                key={String(def.key)}
                def={def}
                current={metrics}
                previous={prev}
                compareEnabled={compareEnabled}
                hasActivity={active}
              />
            ))}
          </div>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}

export function WorkspaceCampaignPlacementMetricCells({
  byPlacement,
  previousByPlacement,
  compareEnabled,
  isLoading,
}: {
  byPlacement: Map<PlacementId, PlacementDerivedMetrics> | null | undefined;
  previousByPlacement?: Map<PlacementId, PlacementDerivedMetrics> | null;
  compareEnabled: boolean;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return (
      <>
        {CAMPAIGN_PLACEMENT_LINES.map((line) => (
          <td key={line.id} className={`${workspaceTableClass.td} app-table-cell-wrap align-top whitespace-normal`}>
            <span className="block h-[4.5rem] w-44 animate-pulse rounded-lg bg-surface-muted" />
          </td>
        ))}
      </>
    );
  }

  return (
    <>
      {CAMPAIGN_PLACEMENT_LINES.map((line) => (
        <td key={line.id} className={`${workspaceTableClass.td} app-table-cell-wrap align-top whitespace-normal`}>
          <WorkspacePlacementMetricCard
            title={line.label}
            current={byPlacement?.get(line.id)}
            previous={previousByPlacement?.get(line.id)}
            compareEnabled={compareEnabled}
          />
        </td>
      ))}
    </>
  );
}
